import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Button, Spinner, Alert, Form, Row, Col } from 'react-bootstrap';
import { FiUpload, FiAlertTriangle, FiArrowLeft } from 'react-icons/fi';
import { importClaimsBulkUpdatesFlower, importClaimsBulkUpdatesOlela, importClaimsBulkUpdatesB2C } from '../../services/api';
import 'bootstrap/dist/css/bootstrap.css';
import './AdminTripsPolicies.css';

const AdminTripsClaimsManagement = () => {
    const navigate = useNavigate();
    const [importFile, setImportFile] = useState(null);
    const [importType, setImportType] = useState(''); // 'olela', 'flower', ou 'b2c'
    const [importLoading, setImportLoading] = useState(false);
    const [importError, setImportError] = useState(null);
    const [importSuccess, setImportSuccess] = useState(null);

    const handleImport = async () => {
        if (!importFile) {
            setImportError('Veuillez sélectionner un fichier Excel');
            return;
        }
        if (!importType || importType.trim() === '') {
            setImportError('Veuillez sélectionner un type d\'import');
            return;
        }

        setImportLoading(true);
        setImportError(null);
        setImportSuccess(null);

        try {
            let importFunction;
            let typeName;

            // Déterminer la fonction d'import selon le type sélectionné
            if (importType === 'olela') {
                importFunction = importClaimsBulkUpdatesOlela;
                typeName = 'Olela';
            } else if (importType === 'flower') {
                importFunction = importClaimsBulkUpdatesFlower;
                typeName = 'Flower';
            } else if (importType === 'b2c') {
                importFunction = importClaimsBulkUpdatesB2C;
                typeName = 'B2C';
            } else {
                setImportError(`Type d'import non reconnu: ${importType}`);
                setImportLoading(false);
                return;
            }

            // Vérifier que la fonction d'import existe
            if (!importFunction) {
                setImportError('Fonction d\'import non disponible');
                setImportLoading(false);
                return;
            }

            // Appel de l'API d'import
            console.log('Début de l\'import:', { type: importType, fileName: importFile.name });
            const result = await importFunction(importFile);
            console.log('Import réussi:', result);

            // Construire le message de succès détaillé
            let successMessage = '';

            if (result) {
                const updatedCount = result.summary?.updated || result.updated_claims?.length || result.successful_updates || 0;
                const failedCount = result.summary?.failed || result.failed_rows?.length || result.failed_updates || 0;

                // Format du message selon les spécifications
                successMessage = `Succès :\n${updatedCount} claims updated successfully\n\nRésumé :\n- Claims mis à jour : ${updatedCount}\n- Échecs : ${failedCount}`;

                // Ajouter les détails des lignes échouées si présentes
                if (result.failed_rows && result.failed_rows.length > 0) {
                    successMessage += `\n\nLignes échouées :`;
                    result.failed_rows.forEach((failedRow, index) => {
                        successMessage += `\n${index + 1}. Ligne ${failedRow.row} (Policy: ${failedRow.policy_code || 'N/A'}) : ${failedRow.error || 'Erreur inconnue'}`;
                    });
                }
                
                // Gérer la structure results.errors si présente
                if (result.results?.errors && Array.isArray(result.results.errors) && result.results.errors.length > 0) {
                    successMessage += `\n\nLignes échouées :`;
                    result.results.errors.forEach((error, index) => {
                        const reservationNumber = error.reservation_number || error.reservationNumber || 'N/A';
                        const errorMsg = error.error || error.message || 'Erreur inconnue';
                        successMessage += `\n${index + 1}. Réservation ${reservationNumber}: ${errorMsg || 'Erreur non spécifiée'}`;
                    });
                }
            } else {
                successMessage = `Succès :\n0 claims updated successfully\n\nRésumé :\n- Claims mis à jour : 0\n- Échecs : 0`;
            }

            setImportSuccess(successMessage);
            setImportFile(null);
            setImportType('');
            // Ne pas fermer automatiquement le modal pour permettre à l'utilisateur de voir les détails
        } catch (error) {
            console.error('Erreur lors de l\'import:', error);
            console.error('Erreur response:', error.response);
            console.error('Erreur response data:', error.response?.data);

            // Extraire le message d'erreur de différentes structures possibles
            let errorMessage = 'Erreur lors de l\'import. Veuillez vérifier votre fichier et réessayer.';

            if (error.response) {
                const responseData = error.response.data;

                // Priorité 1: detail (message principal de l'API)
                if (responseData?.detail) {
                    if (typeof responseData.detail === 'string') {
                        errorMessage = responseData.detail;
                    } else if (responseData.detail?.message) {
                        errorMessage = responseData.detail.message;
                    } else if (responseData.detail?.detail) {
                        errorMessage = responseData.detail.detail;
                    } else {
                        errorMessage = JSON.stringify(responseData.detail);
                    }
                }
                // Priorité 2: message
                else if (responseData?.message) {
                    errorMessage = responseData.message;
                }
                // Priorité 3: error (format alternatif)
                else if (responseData?.error) {
                    errorMessage = typeof responseData.error === 'string'
                        ? responseData.error
                        : responseData.error.message || JSON.stringify(responseData.error);
                }
                // Priorité 4: errors (tableau d'erreurs)
                else if (responseData?.errors) {
                    if (Array.isArray(responseData.errors)) {
                        errorMessage = responseData.errors.map(e => e.message || e.msg || e).join('; ');
                    } else if (typeof responseData.errors === 'object') {
                        errorMessage = Object.entries(responseData.errors)
                            .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
                            .join('; ');
                    }
                }
                // Priorité 5: statusText
                else if (error.response.statusText) {
                    errorMessage = `${error.response.status} ${error.response.statusText}`;
                }
            }
            // Si pas de réponse, utiliser le message de l'erreur
            else if (error.message) {
                errorMessage = error.message;
            }

            setImportError(errorMessage);
        } finally {
            setImportLoading(false);
        }
    };

    return (
        <div className="dashboard-page admin-trips-policies-page">
            <Card>
                <Card.Body>
                    <div className="page-header">
                        <div>
                            <h2 className="page-title">
                                <FiAlertTriangle className="me-2" />
                                Trip - Claims Management
                            </h2>
                            <p className="text-muted mb-0">Import des mises à jour de sinistres depuis Excel</p>
                        </div>
                        <div>
                            <Button
                                variant="outline-secondary"
                                onClick={() => navigate(-1)}
                                className="d-flex align-items-center"
                            >
                                <FiArrowLeft className="me-2" />
                                Retour
                            </Button>
                        </div>
                    </div>

                    {importLoading && (
                        <Alert variant="info" className="mt-3">
                            <Spinner size="sm" className="me-2" animation="border" />
                            <strong>Import en cours...</strong> Cela peut prendre plusieurs minutes selon la taille du fichier. Veuillez patienter.
                        </Alert>
                    )}

                    <Card className="mt-4">
                        <Card.Body>
                            <h5>Instructions</h5>
                            <p>Cette page permet d'importer des mises à jour de sinistres (claims) depuis un fichier Excel.</p>
                            <ul>
                                <li>Sélectionnez le type d'import (Flower, Olela ou B2C)</li>
                                <li>Choisissez le fichier Excel contenant les données à importer</li>
                                <li>Cliquez sur "Importer" pour lancer l'import</li>
                            </ul>
                            <p className="text-muted mb-1">
                                <strong>Formats acceptés:</strong> .xlsx, .xls, .csv
                            </p>
                        </Card.Body>
                    </Card>

                    <Card className="mt-4">
                        <Card.Header>
                            <h5 className="mb-0">Importer un fichier Excel</h5>
                        </Card.Header>
                        <Card.Body>
                            <Form onSubmit={(e) => {
                                e.preventDefault();
                                handleImport();
                            }}>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-bold">
                                                Type d'import <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Select
                                                value={importType}
                                                onChange={(e) => {
                                                    setImportType(e.target.value);
                                                    setImportError(null);
                                                }}
                                                disabled={importLoading}
                                                required
                                                style={{ cursor: importLoading ? 'not-allowed' : 'pointer' }}
                                            >
                                                <option value="">Sélectionner un type d'import</option>
                                                <option value="olela">Olela Claims</option>
                                                <option value="flower">Flower Claims</option>
                                                <option value="b2c">B2C Claims</option>
                                            </Form.Select>
                                            <Form.Text className="text-muted">
                                                Sélectionnez le type de mises à jour de sinistres à importer
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-bold">
                                                Fichier Excel <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="file"
                                                accept=".xlsx,.xls,.csv"
                                                onChange={(e) => {
                                                    const file = e.target.files[0];
                                                    if (file) {
                                                        setImportFile(file);
                                                        setImportError(null);
                                                    }
                                                }}
                                                disabled={importLoading}
                                                style={{ cursor: importLoading ? 'not-allowed' : 'pointer' }}
                                                required
                                            />
                                            <Form.Text className="text-muted d-block mt-2">
                                                Formats acceptés: <strong>.xlsx</strong>, <strong>.xls</strong>, <strong>.csv</strong>
                                            </Form.Text>
                                            {importFile && (
                                                <div className="mt-3 p-2 bg-light rounded">
                                                    <small className="text-success">
                                                        <strong>✓ Fichier sélectionné :</strong> {importFile.name}
                                                        <br />
                                                        <span className="text-muted">Taille : {(importFile.size / 1024 / 1024).toFixed(2)} MB</span>
                                                    </small>
                                                </div>
                                            )}
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <div className="d-flex justify-content-end gap-2 mt-4">
                                    <Button
                                        variant="secondary"
                                        onClick={() => {
                                            setImportFile(null);
                                            setImportType('');
                                            setImportError(null);
                                            setImportSuccess(null);
                                        }}
                                        disabled={importLoading}
                                        type="button"
                                    >
                                        Réinitialiser
                                    </Button>
                                    <Button
                                        variant="success"
                                        type="submit"
                                        disabled={importLoading || !importFile || !importType || importType.trim() === ''}
                                    >
                                        {importLoading ? (
                                            <>
                                                <Spinner size="sm" className="me-2" animation="border" />
                                                Import en cours...
                                            </>
                                        ) : (
                                            <>
                                                <FiUpload className="me-2" />
                                                Importer
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>

                    {importError && (
                        <Alert variant="danger" className="mt-3" dismissible onClose={() => setImportError(null)}>
                            <strong>Erreur :</strong> {importError}
                        </Alert>
                    )}
                    {importSuccess && (
                        <Alert variant="success" className="mt-3" dismissible onClose={() => setImportSuccess(null)}>
                            <pre style={{
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                marginTop: 0,
                                marginBottom: 0,
                                fontSize: '0.9rem',
                                backgroundColor: 'rgba(255, 255, 255, 0.3)',
                                padding: '10px',
                                borderRadius: '4px',
                                fontFamily: 'inherit'
                            }}>{importSuccess}</pre>
                        </Alert>
                    )}
                </Card.Body>
            </Card>
        </div>
    );
};

export default AdminTripsClaimsManagement;

